import React, { useEffect, useRef } from 'react';
import { TouchableOpacity, StyleSheet, Animated, Easing, Platform } from 'react-native';
import { useTheme } from '../context/ThemeContext';

const ToggleSwitch = ({ isOn, onToggle, disabled = false }) => {
  const { palette, mode } = useTheme();
  const thumbPosition = useRef(new Animated.Value(isOn ? 1 : 0)).current;
  const switchBackground = useRef(new Animated.Value(isOn ? 1 : 0)).current;

  useEffect(() => {
    Animated.parallel([
      Animated.timing(thumbPosition, {
        toValue: isOn ? 1 : 0,
        duration: 200,
        easing: Easing.out(Easing.quad),
        useNativeDriver: false,
      }),
      Animated.timing(switchBackground, {
        toValue: isOn ? 1 : 0,
        duration: 200,
        easing: Easing.out(Easing.quad),
        useNativeDriver: false,
      }),
    ]).start();
  }, [isOn]);

  const thumbTranslateX = thumbPosition.interpolate({
    inputRange: [0, 1],
    outputRange: [0, 36], // 72px width - 28px thumb width - 8px total margin = 36px movement
  });

  // Use theme-appropriate colors
  const backgroundColor = switchBackground.interpolate({
    inputRange: [0, 1],
    outputRange: mode === 'dark' 
      ? ['rgba(255, 255, 255, 0.10)', '#FF0600'] // Dark mode: transparent to primary with opacity
      : ['rgba(0, 0, 0, 0.10)', '#FF0600'], // Light mode
  });

  const thumbBackgroundColor = thumbPosition.interpolate({
    inputRange: [0, 1],
    outputRange: mode === 'dark'
      ? ['rgba(255, 255, 255, 0.30)', 'rgba(255, 255, 255, 0.95)']
      : ['rgba(0, 0, 0, 0.30)', 'rgba(255, 255, 255, 0.95)'],
  });

  return (
    <TouchableOpacity
      style={[
        styles.toggleSwitch,
        isOn && styles.toggleSwitchOn,
        disabled && styles.toggleSwitchDisabled
      ]}
      onPress={onToggle}
      disabled={disabled}
      activeOpacity={0.8}
    >
      <Animated.View style={[styles.toggleSwitchBackground, { backgroundColor }]} />
      <Animated.View
        style={[
          styles.toggleThumb,
          isOn && styles.toggleThumbOn,
          { 
            transform: [{ translateX: thumbTranslateX }],
            backgroundColor: thumbBackgroundColor,
          }
        ]}
      />
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  toggleSwitch: {
    width: 72,
    height: 36,
    position: 'relative',
    backgroundColor: 'rgba(255, 255, 255, 0.10)',
    overflow: 'hidden',
    borderRadius: 999,
    borderWidth: 1.5,
    borderColor: 'rgba(255,255,255,0.2)',
  },
  toggleSwitchOn: {
    // Additional styling when on can go here if needed
  },
  toggleSwitchDisabled: {
    opacity: 0.5,
  },
  toggleSwitchBackground: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: 999,
  },
  toggleThumb: {
    width: 28,
    height: 28,
    position: 'absolute',
    left: 4,
    top: 2.5,
    borderRadius: 9999,
    elevation: 2,
    ...Platform.select({
      web: {
        boxShadow: '0px 0px 2px rgba(0, 0, 0, 0.3)',
      },
      default: {
        shadowColor: '#000',
        shadowOpacity: 0.3,
        shadowRadius: 2,
      },
    }),
  },
  toggleThumbOn: {
    elevation: 4,
    ...Platform.select({
      web: {
        boxShadow: '0px 0px 4px rgba(0, 0, 0, 0.5)',
      },
      default: {
        shadowOpacity: 0.5,
        shadowRadius: 4,
      },
    }),
  },
});

export default ToggleSwitch;

